import {
	getAssignmentPresentation,
	getCandidateOption,
	getImportDiagnostics,
	getImportRowIdentity,
	getLocationPresentation,
	getOrderCandidates,
	getServicePresentation,
	isExistingImportOrder,
} from './importReview.model';
import {
	applyContractFrequencySelection,
	getContractFrequencyPresentation,
} from 'components/DataFieldManager/ContractFrequencyId/contractFrequencyReview.model';

const hasValue = value =>
	value !== undefined && value !== null && String(value).trim() !== '';

const uniqueBy = (values, getKey) => {
	const byKey = new Map();
	(values || []).forEach(value => {
		const key = getKey(value);
		if (key && !byKey.has(key)) byKey.set(key, value);
	});
	return Array.from(byKey.values());
};

const intersectBy = (catalogs, getKey) => {
	if (!catalogs.length || catalogs.some(catalog => !catalog.length)) return [];
	const remainingCatalogKeys = catalogs
		.slice(1)
		.map(catalog => new Set(catalog.map(getKey).filter(Boolean)));
	return uniqueBy(catalogs[0], getKey).filter(option =>
		remainingCatalogKeys.every(keys => keys.has(getKey(option))),
	);
};

export const getBulkAssignmentOptionKey = option =>
	hasValue(option?.assignedTechId ?? option?.id)
		? JSON.stringify([
				String(option.assignedTechId ?? option.id),
				hasValue(option?.entityId) ? String(option.entityId) : null,
			])
		: undefined;

export const getBulkServiceOptionKey = option =>
	hasValue(option?.id) ? String(option.id) : undefined;

export const getBulkLocationOptionKey = option => {
	const candidate = getCandidateOption(option);
	return [candidate.cityId, candidate.townshipId, candidate.zoneId].every(
		hasValue,
	)
		? JSON.stringify(
				[candidate.cityId, candidate.townshipId, candidate.zoneId].map(String),
			)
		: undefined;
};

export const getSelectedImportOrders = (orders, selectedRowIds) => {
	const selectedIds = new Set(
		(selectedRowIds || []).filter(hasValue).map(String),
	);
	return (orders || []).filter(order =>
		selectedIds.has(String(getImportRowIdentity(order))),
	);
};

export const getBulkContractFrequencyOptionKey = option => {
	const existingId = option?.existing?.frequencyId;
	const requestedId = option?.requested?.frequencyId;
	return hasValue(existingId) && hasValue(requestedId)
		? `${existingId}->${requestedId}`
		: undefined;
};

export const getBulkContractFrequencyOptions = orders => {
	const groups = new Map();
	(orders || []).forEach(order => {
		const frequency = getContractFrequencyPresentation(order);
		const rowId = getImportRowIdentity(order);
		const transition = {
			existing: frequency.existing,
			requested: frequency.requested,
		};
		const key = getBulkContractFrequencyOptionKey(transition);
		if (
			frequency.status !== 'review_required' ||
			!frequency.canConfirmRequested ||
			!key ||
			!hasValue(rowId)
		)
			return;
		if (!groups.has(key))
			groups.set(key, {
				...transition,
				targetRowIds: [],
			});
		groups.get(key).targetRowIds.push(String(rowId));
	});
	const options = Array.from(groups.values()).sort((left, right) =>
		getBulkContractFrequencyOptionKey(left).localeCompare(
			getBulkContractFrequencyOptionKey(right),
		),
	);
	return {
		options,
		disabledReason: options.length
			? undefined
			: 'No hay cambios de frecuencia con autorización vigente para confirmar en lote. Valida nuevamente o corrígelos fuera de la importación.',
	};
};

const hasOwn = (value, key) =>
	Object.prototype.hasOwnProperty.call(value || {}, key);

const captureContractFrequencyState = (order, field) => ({
	fields: Array.from(new Set([field?.id, field?.name].filter(Boolean))).map(
		fieldId => ({
			fieldId,
			present: hasOwn(order, fieldId),
			value: order?.[fieldId],
		}),
	),
	diagnostics: ['frequencyMatch', 'frequency_match'].map(key => ({
		key,
		present: hasOwn(order?.importDiagnostics, key),
		value: order?.importDiagnostics?.[key],
	})),
});

const sameRowIds = (left, right) => {
	const leftIds = (left || []).map(String).sort();
	const rightIds = (right || []).map(String).sort();
	return (
		leftIds.length === rightIds.length &&
		leftIds.every((rowId, index) => rowId === rightIds[index])
	);
};

const failedFrequencyTransition = (orders, detail) => ({
	ok: false,
	orders,
	selections: [],
	error: `No se aplicó ningún cambio de frecuencia. ${detail}`,
});

export const prepareBulkContractFrequencyTransition = ({
	orders,
	transition,
	field,
	existingSelections = [],
}) => {
	if (!field?.id)
		return failedFrequencyTransition(
			orders,
			'La plantilla ya no incluye el campo de frecuencia contractual.',
		);
	const transitionKey = getBulkContractFrequencyOptionKey(transition);
	const requestedTargetIds = Array.from(
		new Set((transition?.targetRowIds || []).filter(hasValue).map(String)),
	);
	if (!transitionKey || !requestedTargetIds.length)
		return failedFrequencyTransition(
			orders,
			'La transición seleccionada ya no está disponible.',
		);

	const authoritativeTransition = getBulkContractFrequencyOptions(
		orders,
	).options.find(
		option => getBulkContractFrequencyOptionKey(option) === transitionKey,
	);
	if (
		!authoritativeTransition ||
		!sameRowIds(authoritativeTransition.targetRowIds, requestedTargetIds)
	)
		return failedFrequencyTransition(
			orders,
			'Las filas o la transición cambiaron; valida nuevamente la importación.',
		);

	const ordersByRowId = new Map();
	(orders || []).forEach(order => {
		const rowId = getImportRowIdentity(order);
		if (!hasValue(rowId)) return;
		const key = String(rowId);
		if (ordersByRowId.has(key)) ordersByRowId.set(key, null);
		else ordersByRowId.set(key, order);
	});
	const candidatesByRowId = new Map();
	const challenges = new Set();
	for (const rowId of requestedTargetIds) {
		const order = ordersByRowId.get(rowId);
		const frequency = getContractFrequencyPresentation(order);
		const currentKey = getBulkContractFrequencyOptionKey({
			existing: frequency.existing,
			requested: frequency.requested,
		});
		const candidate = frequency.candidates.find(
			item =>
				String(item.frequencyId) ===
				String(authoritativeTransition.requested.frequencyId),
		);
		if (
			!order ||
			frequency.status !== 'review_required' ||
			frequency.mutable !== true ||
			currentKey !== transitionKey ||
			!candidate?.selectionChallenge ||
			challenges.has(candidate.selectionChallenge)
		)
			return failedFrequencyTransition(
				orders,
				'Una fila perdió su autorización individual; valida nuevamente antes de reintentar.',
			);
		challenges.add(candidate.selectionChallenge);
		candidatesByRowId.set(rowId, candidate);
	}

	const previousSelectionsByRowId = new Map(
		(existingSelections || []).map(selection => [
			String(selection?.rowId),
			selection,
		]),
	);
	const stagedByRowId = new Map();
	const selections = requestedTargetIds.map(rowId => {
		const order = ordersByRowId.get(rowId);
		const candidate = candidatesByRowId.get(rowId);
		const stagedOrder = applyContractFrequencySelection({
			order,
			option: candidate,
			field,
		});
		stagedByRowId.set(rowId, stagedOrder);
		return {
			rowId,
			frequencyId: candidate.frequencyId,
			challenge: candidate.selectionChallenge,
			validated: false,
			previousState:
				previousSelectionsByRowId.get(rowId)?.previousState ||
				captureContractFrequencyState(order, field),
		};
	});
	const targetIds = new Set(requestedTargetIds);
	return {
		ok: true,
		orders: (orders || []).map(
			order => stagedByRowId.get(String(getImportRowIdentity(order))) || order,
		),
		selections: [
			...(existingSelections || []).filter(
				selection => !targetIds.has(String(selection?.rowId)),
			),
			...selections,
		],
		targetRowIds: requestedTargetIds,
		transition: authoritativeTransition,
	};
};

const emptySelection = {
	options: [],
	disabledReason: 'Selecciona al menos una orden para usar acciones masivas.',
};

export const getBulkAssignmentOptions = (orders, assignedTechIdField) => {
	if (!orders.length) return emptySelection;
	if (!assignedTechIdField)
		return {
			options: [],
			disabledReason: 'La plantilla no incluye el campo de técnico.',
		};
	if (
		orders.some(order => getImportDiagnostics(order).needsRevalidation === true)
	)
		return {
			options: [],
			disabledReason:
				'Hay cambios pendientes de validar. Valídalos antes de elegir técnico.',
		};
	const presentations = orders.map(order =>
		getAssignmentPresentation(order, assignedTechIdField),
	);
	const immutable = presentations.find(
		presentation => presentation.mutability.allowed !== true,
	);
	if (immutable)
		return {
			options: [],
			disabledReason:
				immutable.mutability.message ||
				'Alguna orden seleccionada no permite cambiar el técnico.',
		};
	const catalogs = presentations.map(presentation =>
		presentation.candidates.filter(
			candidate =>
				getBulkAssignmentOptionKey(candidate) && candidate.selectionChallenge,
		),
	);
	const options = intersectBy(catalogs, getBulkAssignmentOptionKey);
	return {
		options,
		disabledReason: options.length
			? undefined
			: 'No hay un técnico elegible en común para todas las órdenes seleccionadas. Valida los cambios o reduce la selección.',
	};
};

export const getBulkServiceOptions = orders => {
	if (!orders.length) return emptySelection;
	if (orders.some(isExistingImportOrder))
		return {
			options: [],
			disabledReason:
				'Las órdenes existentes conservan su servicio. Retíralas de la selección.',
		};
	const catalogs = orders.map(order =>
		getServicePresentation(order).candidates.filter(
			candidate =>
				getBulkServiceOptionKey(candidate) && candidate.selectionChallenge,
		),
	);
	const options = intersectBy(catalogs, getBulkServiceOptionKey);
	return {
		options,
		disabledReason: options.length
			? undefined
			: 'No hay un servicio firmado en común para todas las órdenes seleccionadas. Valida los cambios o reduce la selección.',
	};
};

const getLocationCatalog = (order, locationFields, zoneIdField) => {
	const presentation = getLocationPresentation({
		order,
		...locationFields,
		zoneIdField,
	});
	return uniqueBy(
		[
			getCandidateOption(presentation.option),
			...getOrderCandidates(order, zoneIdField?.id),
		].filter(getBulkLocationOptionKey),
		getBulkLocationOptionKey,
	);
};

export const getBulkLocationOptions = (orders, locationFields, zoneIdField) => {
	if (!orders.length) return emptySelection;
	if (
		!zoneIdField ||
		!locationFields?.cityIdField ||
		!locationFields?.townshipIdField
	)
		return {
			options: [],
			disabledReason:
				'La plantilla no incluye la jerarquía completa para esta zona.',
		};
	if (orders.some(isExistingImportOrder))
		return {
			options: [],
			disabledReason:
				'Las órdenes existentes conservan su ubicación. Retíralas de la selección.',
		};
	const options = intersectBy(
		orders.map(order => getLocationCatalog(order, locationFields, zoneIdField)),
		getBulkLocationOptionKey,
	);
	return {
		options,
		disabledReason: options.length
			? undefined
			: 'No hay una ubicación validada en común para todas las órdenes seleccionadas. Valida los cambios o reduce la selección.',
	};
};
